import { useEffect } from 'react'
import { motion } from 'motion/react'
import { FugueContactForm } from '@/components/FugueContactForm'
import { useScrollReveal, useStaggeredGrid, useHeroReveal } from '@gjl/motion/css'
import { Check, Music, Users, Building2, GraduationCap, Church, ArrowRight } from 'lucide-react'

const industryIcons = {
  'Classical Music Festivals & Summer Institutes': Music,
  'Symphony Orchestras & Chamber Ensembles': Users,
  'Opera Companies & Music Theater': Building2,
  'Choral Organizations & Sacred Music Programs': Church,
  'Conservatories & Music Education Organizations': GraduationCap,
}

const industries = [
  {
    title: 'Classical Music Festivals & Summer Institutes',
    subtitle: 'Student programs, professional festivals, and artist residencies',
    description:
      'Summer training programs, chamber music festivals, early music festivals, contemporary music festivals, specialized instrument programs, conservatory-affiliated institutes',
    items: [
      'Modern festival websites with CMS for easy updates',
      'Application and enrollment funnels with payment processing',
      'Automated email sequences for applicants and attendees',
      'Faculty bios, program schedules, and concert calendars',
      'Analytics and enrollment tracking',
    ],
    examples: ['Chroma International Music Festival', 'Chamber music institutes', 'Youth orchestras'],
  },
  {
    title: 'Symphony Orchestras & Chamber Ensembles',
    subtitle: 'Professional orchestras, chamber groups, and period instrument ensembles',
    description:
      'Regional orchestras, professional chamber music organizations, baroque ensembles, contemporary music groups, period instrument orchestras',
    items: [
      'Concert season websites with integrated ticketing',
      'Donor management and fundraising campaign support',
      'Concert program notes and artist biographies',
      'Email marketing for subscribers and single-ticket buyers',
      'Media galleries for recordings and performance videos',
    ],
    examples: ['Regional orchestras', 'Chamber music societies', 'Baroque orchestras', 'Contemporary ensembles'],
  },
  {
    title: 'Opera Companies & Music Theater',
    subtitle: 'Opera companies, early opera ensembles, and musical theater organizations',
    description:
      'Regional opera companies, contemporary opera ensembles, baroque opera groups, music theater companies, summer opera festivals',
    items: [
      'High-impact websites showcasing productions and artists',
      'Season subscription and single-ticket sales integration',
      'Production calendars with cast and creative team information',
      'Multimedia galleries for trailers, photos, and recordings',
      'Donor and patron management systems',
    ],
    examples: ['Regional opera companies', 'Contemporary opera ensembles', 'Summer opera programs'],
  },
  {
    title: 'Choral Organizations & Sacred Music Programs',
    subtitle: 'Community choruses, church music departments, and vocal ensembles',
    description:
      'Community choruses, professional chamber choirs, church music programs, cathedral choral foundations, sacred music concert series, early music vocal ensembles',
    items: [
      'Concert series websites with event calendars',
      'Ticketing for concerts and fundraising events',
      'Member directories and rehearsal schedules',
      'Donation systems for program support',
      'Media libraries for recordings and performance archives',
    ],
    examples: ['Community choruses', 'Cathedral music programs', 'Chamber choirs', 'Sacred music series'],
  },
  {
    title: 'Conservatories & Music Education Organizations',
    subtitle: 'Degree programs, pre-college divisions, and community music schools',
    description:
      'Conservatory pre-college programs, community music schools, continuing education programs, youth orchestras, music education nonprofits',
    items: [
      'Admissions funnels and application systems',
      'Student and parent portals for program information',
      'Program and course catalogs with registration',
      'CRM integration with institutional systems',
      'Compliance, accessibility, and security features',
    ],
    examples: ['Pre-college programs', 'Community music schools', 'Youth orchestra programs'],
  },
]

export default function WhoWeHelpPage() {
  useEffect(() => {
    document.title = 'Who We Help | Fugue Lab'
  }, [])

  // Animation refs
  const heroRef = useHeroReveal<HTMLElement>(0.2)
  const industriesRef = useStaggeredGrid<HTMLElement>('.industry-card', { stagger: 0.12 })
  const ctaRef = useScrollReveal<HTMLElement>('.cta-content')

  return (
    <div className="mx-auto max-w-6xl space-y-16 px-4 py-12">
      <section ref={heroRef} className="space-y-6 text-center">
        <h1 className="text-fluid-h1">Who We Help</h1>
        <p className="text-muted-foreground mx-auto max-w-2xl text-lg leading-relaxed">
          Fugue Lab specializes in digital strategy for classical music organizations. We understand the unique
          challenges of festivals, orchestras, chamber ensembles, opera companies, choral organizations, sacred music
          programs, and music education institutions.
        </p>
      </section>

      <section ref={industriesRef} className="space-y-8">
        {industries.map(({ title, subtitle, description, items, examples }) => {
          const Icon = industryIcons[title as keyof typeof industryIcons] || Music
          return (
            <motion.div
              key={title}
              className="industry-card group border-border/50 from-primary/5 hover:border-primary/30 relative rounded-2xl border bg-gradient-to-b to-transparent p-6 transition-all duration-300 hover:shadow-lg md:p-8"
              whileHover={{ y: -4 }}
              transition={{ type: 'spring', stiffness: 300, damping: 20 }}
            >
              <div className="flex flex-col gap-6 md:flex-row">
                {/* Icon */}
                <div className="flex-shrink-0">
                  <div className="bg-primary/10 text-primary inline-flex h-14 w-14 items-center justify-center rounded-xl transition-transform duration-300 group-hover:scale-110">
                    <Icon className="h-7 w-7" />
                  </div>
                </div>

                {/* Content */}
                <div className="flex-1 space-y-4">
                  <div>
                    <h2 className="text-xl font-bold">{title}</h2>
                    <p className="text-muted-foreground mt-1">{subtitle}</p>
                  </div>
                  <p className="text-muted-foreground text-sm">{description}</p>

                  <div className="grid gap-x-6 gap-y-2 md:grid-cols-2">
                    {items.map((item) => (
                      <div key={item} className="flex items-start gap-2">
                        <Check className="text-primary mt-0.5 h-4 w-4 flex-shrink-0" strokeWidth={2.5} />
                        <span className="text-sm">{item}</span>
                      </div>
                    ))}
                  </div>

                  <div className="flex flex-wrap gap-2 pt-2">
                    {examples.map((example) => (
                      <span
                        key={example}
                        className="bg-primary/10 text-primary rounded-full px-3 py-1.5 text-xs font-medium"
                      >
                        {example}
                      </span>
                    ))}
                  </div>
                </div>
              </div>
            </motion.div>
          )
        })}
      </section>

      <section
        ref={ctaRef}
        className="cta-content from-primary/10 via-primary/5 border-primary/20 space-y-6 rounded-2xl border bg-gradient-to-br to-transparent p-8 text-center md:p-12"
      >
        <h2 className="text-fluid-h3">Not Sure If We're a Fit?</h2>
        <p className="text-muted-foreground mx-auto max-w-2xl">
          If you're running a classical music organization—whether a festival, chamber ensemble, opera company, chorus,
          sacred music program, or music school—and need help with your website, ticketing systems, or digital strategy,
          we'd love to talk.
        </p>
        <motion.a
          href="/book"
          target="_blank"
          rel="noopener noreferrer"
          className="text-primary inline-flex cursor-pointer items-center gap-2 font-medium hover:underline"
          whileHover={{ x: 4 }}
          transition={{ type: 'spring', stiffness: 300, damping: 20 }}
        >
          Book a Free Strategy Call <ArrowRight className="h-4 w-4" />
        </motion.a>
      </section>

      <FugueContactForm />
    </div>
  )
}
